import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, CircleCheck, Clock } from 'lucide-react'
import {
  Button, Card, CardHeader, EmptyState, ErrorState, Field, FieldInput, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, fetchAreas, formatCount, formatDateTime, formatMinutes, may, minutesLate,
  nameIn,
  recordAreaClean, setAreaService, translateError,
  type AreaBoardRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-30 · PUBLIC AREAS. The other half of housekeeping, and the half nobody sells.

   THE ROOM BOARD ASKS "CAN I SELL IT?". THIS ASKS "IS ANYBODY EMBARRASSED BY IT RIGHT
   NOW?" — and the answers do not have the same shape, which is why this is a list and
   not a grid of tiles. A room board is scanned for one colour among five hundred; an
   area board is READ, forty rows at most, and the useful information is in the numbers
   beside each one.

   GROUPED BY ZONE, NOT BY FLOOR, and that is the client's structure (CQ-04, CQ-87)
   rather than ours: a washroom is on no floor and a corridor is on one. Grouping by
   floor would put the two ground-floor washrooms in two different places.

   THE RATIO IS FOR THE MACHINE; THE ROW SAYS MINUTES. The server sends lateness as a
   fraction of the interval so that one list can sort a ninety-minute washroom round
   against a weekly deep clean. Showing a supervisor "1.5" would be showing them nothing.
   Multiplied back by the interval it is "three hours late", which is a fact somebody can
   act on — and that multiplication is the only arithmetic this screen does.
   ========================================================================= */

export function AreaBoard({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()
  const [confirmation, setConfirmation] = useState<string | null>(null)
  /** Which row's close-or-reopen panel is open. One at a time: two reason boxes on one
   *  screen is #152's defect by another route. */
  const [editing, setEditing] = useState<string | null>(null)

  const areas = useQuery({
    queryKey: ['areas', propertyId],
    queryFn: ({ signal }) => fetchAreas(propertyId, { signal }),
    /* A minute. A washroom on a ninety-minute cadence crosses from due-soon to
       overdue in about twenty-two minutes, so a board refreshed every five would be
       telling a supervisor something that stopped being true four minutes ago. */
    refetchInterval: 60_000,
    retry: false,
  })

  const clean = useMutation({
    mutationFn: (row: AreaBoardRow) =>
      recordAreaClean(propertyId, row.areaId, { capturedAtUtc: new Date().toISOString() }),
    onSuccess: (_updated, row) => {
      setConfirmation(t.areasMarked.replace('{name}', nameIn(locale, row.name, row.nameAr)))
      void queryClient.invalidateQueries({ queryKey: ['areas', propertyId] })
    },
  })

  /* **CLOSING ONE BY HAND** (Sprint 57). Its own mutation rather than a flag on the one
     above, because they fail differently and a shared error state would show a refusal
     about a reason under a button about cleaning. */
  const service = useMutation({
    mutationFn: (v: { row: AreaBoardRow; outOfService: boolean; reason: string }) =>
      setAreaService(propertyId, v.row.areaId, {
        outOfService: v.outOfService, reason: v.reason,
      }),
    onSuccess: (_updated, v) => {
      const name = nameIn(locale, v.row.name, v.row.nameAr)
      setConfirmation((v.outOfService ? t.areaSvcClosed : t.areaSvcReopened)
        .replace('{name}', name))
      setEditing(null)
      void queryClient.invalidateQueries({ queryKey: ['areas', propertyId] })
    },
  })

  const rows = areas.data ?? []

  /* COUNTED FROM THE ROWS ON SCREEN, never sent alongside them. A banner reading
     "4 need attention" beside a list showing three is the fastest way to lose a
     supervisor's trust in every number on the page. */
  const needAttention = useMemo(
    () => rows.filter((r) => r.standing === 'OVERDUE' || r.standing === 'MISSED'),
    [rows])

  /* Zones in the order the server sent their first row, which is worst-first — so the
     zone holding the most neglected area is the zone at the top. Sorting the groups
     alphabetically would bury it. */
  const zones = useMemo(() => {
    const grouped = new Map<string, { label: string; rows: AreaBoardRow[] }>()
    for (const row of rows) {
      const key = row.zoneId ?? '~none'
      if (!grouped.has(key)) {
        grouped.set(key, {
          label: row.zoneName
            ? nameIn(locale, row.zoneName, row.zoneNameAr)
            : t.areasUngrouped,
          rows: [],
        })
      }
      grouped.get(key)!.rows.push(row)
    }
    return [...grouped.values()]
  }, [rows, locale, t.areasUngrouped])

  if (areas.isLoading && !areas.error) return <LoadingState rows={6} />

  if (areas.error && !areas.data) {
    if (areas.error instanceof ApiError && areas.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(areas.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={areas.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  if (rows.length === 0) return <EmptyState title={t.areasEmpty} />

  const mayClean = may('area.clean')
  /* Its own grant and so its own check, like the room's. A hotel that has moved one and
     not the other gets exactly what it set. */
  const maySetService = may('area.setOutOfService')

  return (
    <div className="flex flex-col gap-3.5">

      {confirmation && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-hk border border-[#86efac] bg-[#f0fdf4]
                     px-3 py-2 text-[12.5px] text-[#166534]
                     dark:border-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]"
        >
          <CircleCheck className="size-4 shrink-0" />
          <span>{confirmation}</span>
        </div>
      )}

      <div
        role="status"
        className={`flex items-center gap-2 rounded-hk border px-3 py-2 text-[12.5px] ${
          needAttention.length > 0
            ? 'border-[#fcd34d] bg-[#fffbeb] text-[#b45309] dark:border-[#b45309] dark:bg-[#2a1e08] dark:text-[#fbbf24]'
            : 'border-border bg-muted/40 text-muted-foreground'}`}
      >
        <Clock className="size-4 shrink-0" />
        <span>
          {needAttention.length > 0
            ? t.areasNeedAttention.replace('{count}', formatCount(needAttention.length, locale))
            : t.areasAllOnSchedule}
        </span>
      </div>

      {zones.map((zone) => (
        <Card key={zone.label}>
          {/* NO SUBTITLE. It used to repeat the screen's own — "Grouped by zone, not by
              floor" — under every zone heading, which said the same thing three times on
              one page and told a reader nothing about THIS zone. */}
          <CardHeader title={zone.label} icon={<Building2 className="size-4" />} />
          <div className="flex flex-col">
            {zone.rows.map((row) => (
              <AreaRow
                key={row.areaId}
                row={row}
                locale={locale}
                mayClean={mayClean}
                maySetService={maySetService}
                busy={clean.isPending && clean.variables?.areaId === row.areaId}
                onClean={() => clean.mutate(row)}
                open={editing === row.areaId}
                onToggle={() => {
                  service.reset()
                  setConfirmation(null)
                  setEditing(editing === row.areaId ? null : row.areaId)
                }}
                settingService={service.isPending && service.variables?.row.areaId === row.areaId}
                serviceError={
                  service.variables?.row.areaId === row.areaId ? service.error : null}
                onSetService={(reason) =>
                  service.mutate({ row, outOfService: row.service === 'IN_SERVICE', reason })}
              />
            ))}
          </div>
        </Card>
      ))}
    </div>
  )
}

/** Colours per standing. THREE OF THE SEVEN ARE DELIBERATELY GREY — closed, resting and
 *  being-cleaned are not late, and painting them amber beside a genuinely neglected
 *  corridor is how a supervisor learns to stop reading the colour at all. */
const TONE: Record<string, string> = {
  MISSED: 'border-[#fca5a5] bg-[#fef2f2] text-[#b91c1c] dark:border-[#b91c1c] dark:bg-[#2b0f0f] dark:text-[#fca5a5]',
  OVERDUE: 'border-[#fcd34d] bg-[#fffbeb] text-[#b45309] dark:border-[#b45309] dark:bg-[#2a1e08] dark:text-[#fbbf24]',
  DUE_SOON: 'border-border bg-secondary text-foreground',
  ON_SCHEDULE: 'border-[#86efac] bg-[#f0fdf4] text-[#166534] dark:border-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]',
  BEING_CLEANED: 'border-border bg-muted text-muted-foreground',
  CLOSED: 'border-border bg-muted text-muted-foreground',
  RESTING: 'border-border bg-muted text-muted-foreground',
}

/** Named placeholders, because Arabic and English do not order their clauses the same. */
function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole)
}

function AreaRow(
  {
    row, locale, mayClean, maySetService, busy, onClean,
    open, onToggle, settingService, serviceError, onSetService,
  }:
  {
    row: AreaBoardRow
    locale: 'en' | 'ar'
    mayClean: boolean
    maySetService: boolean
    busy: boolean
    onClean: () => void
    open: boolean
    onToggle: () => void
    settingService: boolean
    serviceError: unknown
    onSetService: (reason: string) => void
  },
) {
  const t = STRINGS[locale]
  const late = minutesLate(row)
  const [reason, setReason] = useState('')

  /* **THE THREE STATES ARE NOT TWO.** `CLOSED` on the chip covers both "somebody shut it"
     and "a fault shut it", and they are different acts with different ways back — so the
     panel reads `service` rather than `standing`. Drawing a reopen button over an area a
     fault has out of order would be a button the server refuses. */
  const closedByHand = row.service === 'OUT_OF_SERVICE'
  const outOfOrder = row.service === 'OUT_OF_ORDER'
  const name = nameIn(locale, row.name, row.nameAr)

  /* WHAT THE ROW SAYS ABOUT TIME, in one phrase, chosen by standing rather than by
     arithmetic — so a shut room never reads "0 minutes late" and an area nobody has ever
     cleaned says so instead of showing a number it does not have. */
  const timing =
    row.lastCompletedUtc === null ? t.areasNeverCleaned
    /* NOTHING, for the three standings that suppress lateness. The chip beside this
       already says "Closed"; printing it twice on one row is not emphasis. */
    : row.standing === 'CLOSED' || row.standing === 'RESTING' || row.standing === 'BEING_CLEANED'
      ? ''
    : late !== null && late > 0
      ? t.areasLateBy.replace('{duration}', formatMinutes(late, locale))
    : row.latenessRatio !== null
      ? t.areasDueIn.replace(
          '{duration}',
          formatMinutes(Math.round(-row.latenessRatio * row.intervalMinutes), locale))
      : ''

  return (
    <div className="border-b border-border/60 last:border-0">
    <div className="flex items-center gap-3 px-3 py-2.5">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <bdi className="truncate text-[13.5px] font-medium text-foreground">
          {nameIn(locale, row.name, row.nameAr)}
        </bdi>
        {/* THE KIND USED TO BE HERE and it read "Washroom · Front of house", because an
            area is normally named after its kind. The cadence and the last clean are what a
            supervisor cannot work out from the name, so they get the line instead. */}
        <span className="text-[11.5px] text-muted-foreground">
          {t.areasEvery.replace('{interval}', formatMinutes(row.intervalMinutes, locale))}
          {/* The last clean, WHEN THERE IS ONE. "never cleaned" already appears in the
              timing column beside the chip, and printing it twice on one row was the
              second repetition this screen shipped with — both found by looking at it
              rather than by reading it. */}
          {row.lastCompletedUtc !== null ? (
            <>
              {' · '}
              {t.areasLastDone.replace(
                '{when}', formatDateTime(new Date(row.lastCompletedUtc), locale))}
            </>
          ) : null}
        </span>
      </div>

      <span className="hidden text-[12px] text-muted-foreground sm:inline">{timing}</span>

      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
        TONE[row.standing] ?? TONE.ON_SCHEDULE}`}
      >
        {t.areaStandings[row.standing]}
      </span>

      {/* OFFERED ONLY WHERE IT MEANS SOMETHING. A shut area cannot be cleaned — the
          server refuses it — so the button is not drawn rather than drawn and refused. */}
      {mayClean && row.standing !== 'CLOSED' ? (
        <button
          type="button"
          onClick={onClean}
          disabled={busy}
          aria-busy={busy}
          className="shrink-0 rounded-hk border border-border px-2.5 py-1 text-[11.5px]
                     font-medium text-foreground hover:bg-muted disabled:opacity-50 cursor-pointer"
        >
          {t.areasMarkClean}
        </button>
      ) : null}

      {/* **DRAWN EVEN WHEN A FAULT HAS IT OUT OF ORDER**, unlike the clean button, and the
          difference is deliberate: "you cannot clean a shut area" is obvious from the chip,
          while "you cannot reopen this one, the fault is the way back" is exactly what a
          supervisor needs told. The panel says it; hiding the control would leave them
          hunting for a button that was never there. */}
      {maySetService ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={fill(closedByHand ? t.areaSvcReopenTitle : t.areaSvcCloseTitle,
            { area: name })}
          className="shrink-0 rounded-hk border border-border px-2.5 py-1 text-[11.5px]
                     font-medium text-foreground hover:bg-muted cursor-pointer"
        >
          {closedByHand ? t.areaSvcReopen : t.areaSvcClose}
        </button>
      ) : null}
    </div>

    {maySetService && open ? (
      <div className="flex flex-col gap-2 border-t border-border/60 bg-muted/30 px-3 py-3">
        <span className="text-[12px] font-semibold">
          <bdi>{fill(closedByHand ? t.areaSvcReopenTitle : t.areaSvcCloseTitle,
            { area: name })}</bdi>
        </span>

        {/* **THE STATE FIRST, IN A SENTENCE.** A control whose label flips between "close"
            and "reopen" is one somebody presses to find out which it currently is. */}
        {closedByHand ? (
          <span className="text-[12px] text-muted-foreground">{t.areaSvcClosedNow}</span>
        ) : null}

        {outOfOrder ? (
          /* Amber and not red: nothing is broken about the product and nobody has done
             anything wrong — the area is out of order because a fault was raised, and the
             way back is through the fault. */
          <div
            role="status"
            className="rounded-hk border border-[#fcd34d] bg-[#fffbeb] px-3 py-2 text-[12px]
                       text-[#92400e] dark:border-[#b45309] dark:bg-[#78350f]/25
                       dark:text-[#fcd34d]"
          >
            {t.areaSvcOutOfOrderNote}
          </div>
        ) : (
          <>
            <span className="text-[12px] leading-relaxed text-muted-foreground">
              {closedByHand ? t.areaSvcReopenIntro : t.areaSvcCloseIntro}
            </span>

            <Field
              label={t.areaSvcReasonLabel}
              hint={closedByHand ? t.areaSvcReopenReasonHint : t.areaSvcCloseReasonHint}
            >
              {({ id, 'aria-label': label }) => (
                <FieldInput
                  id={id}
                  aria-label={`${label} — ${row.code}`}
                  dir="auto"
                  maxLength={500}
                  value={reason}
                  disabled={settingService}
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
            </Field>

            {serviceError ? (
              <div
                role="alert"
                className="rounded-hk border border-[#fca5a5] bg-[#fef2f2] px-3 py-2
                           text-[12px] text-[#b91c1c] dark:border-[#7f1d1d]
                           dark:bg-[#2a0f0f] dark:text-[#fca5a5]"
              >
                {String(translateError(serviceError, locale).message)}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={reason.trim().length === 0 || settingService}
                aria-busy={settingService}
                onClick={() => onSetService(reason.trim())}
              >
                {closedByHand ? t.areaSvcReopen : t.areaSvcClose}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={onToggle}>
                {t.areaSvcCancel}
              </Button>
            </div>
          </>
        )}
      </div>
    ) : null}
    </div>
  )
}
